/**
 * Zusterfoto: een artikel zonder eigen foto leent het beeld van een artikel uit dezelfde serie.
 *
 * Waarom. 114 artikelen in de catalogus hebben helemaal géén foto-URL (Timmers levert er geen).
 * Dat zijn vrijwel allemaal maatvarianten binnen één serie: Diamantboor 10 t/m 95 mm zonder beeld,
 * terwijl Diamantboor 3 mm en 14 mm er wél een hebben. Zonder foto valt zo'n artikel uit de
 * Merchant Center-feed (Google eist een afbeelding) en oogt de categoriepagina rommelig.
 *
 * Hoe. Alleen lenen binnen dezelfde rubriek + groep + subgroep én met hetzelfde eerste woord in de
 * naam ("Diamantboor", "Glaszuiger"). Van de kandidaten wint degene die de langste reeks woorden
 * vooraan deelt; bij gelijkspel de kortste naam, en daarna alfabetisch op slug — zodat dezelfde
 * catalogus altijd dezelfde foto oplevert (geen wisselende feed).
 *
 * Bewust streng: liever twaalf artikelen zónder foto dan één artikel met de foto van iets anders.
 * De guard-test bewaakt dat er nooit over groepen heen geleend wordt.
 */
export type ZusterArtikel = {
  slug: string;
  name: string;
  category?: string;
  groep?: string;
  subgroep?: string;
  image?: string;
};

const normaliseer = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/** Naam in losse woorden; cijfers en maten blijven staan zodat "10" en "10mm" niet samenvallen. */
export function naamWoorden(naam: string): string[] {
  return normaliseer(naam)
    .split(/[^a-z0-9+]+/)
    .filter(Boolean);
}

/** Maat-eenheden; die verdwijnen samen met de getallen uit de vergelijking. */
const EENHEDEN = ["mm", "cm", "m", "mtr", "m1", "kg", "gr", "g", "ml", "l", "st", "stuks", "stuk", "x", "o"];

/**
 * De naam zónder maten: alles met een cijfer erin en alle losse eenheden eruit. Twee artikelen
 * mogen elkaars foto alleen lenen als wat er dán overblijft exact gelijk is — dus "Diamantboor
 * 10mm" en "Diamantboor 3mm" wel, maar "STRIMA Verlenging 20 mm" en "STRIMA keermessen" niet
 * (alleen het merk gemeen) en "Set deurkrukken 90° hoek kokervormig massief" en "Set deurkrukken
 * 90° hoek" evenmin (andere krukvorm).
 */
function naamZonderMaten(woorden: readonly string[]): string {
  return woorden.filter((w) => !/[0-9]/.test(w) && !EENHEDEN.includes(w)).join(" ");
}

const zelfdeSerie = (a: ZusterArtikel, b: ZusterArtikel): boolean =>
  a.category === b.category && a.groep === b.groep && a.subgroep === b.subgroep;

/**
 * Kleur- en afwerkingstermen. Een maatvariant mag de foto van zijn zusje lenen, een KLEURvariant
 * niet: "Douchescharnier Balena ... Mat zwart" kreeg zo de glans-chromen foto, en dat is precies
 * de fout waar Google een product voor afkeurt (afbeelding komt niet overeen met het product).
 * Verschilt de kleur of de afwerking, dan lenen we liever niets.
 */
const KLEUREN = [
  "chroom", "zwart", "wit", "rvs", "inox", "messing", "brons", "bronze", "goud", "nikkel",
  "aluminium", "ral", "antraciet", "koper", "grijs", "blank", "mat", "glans", "gepolijst",
  "geborsteld", "geborst", "gunmetal", "champagne", "titanium", "groen", "blauw", "rood",
  "look", "satin", "nikkelmat",
];

function kleurTermen(woorden: readonly string[]): string {
  return woorden
    .filter((w) => KLEUREN.includes(w))
    .slice()
    .sort()
    .join("+");
}

/**
 * Geeft de foto van het meest gelijkende artikel uit dezelfde serie, of undefined als er geen
 * veilige kandidaat is. Artikelen die zelf een foto hebben, krijgen hier niets terug — die roepen
 * dit ook niet aan.
 */
export function zusterFoto(artikel: ZusterArtikel, catalogus: readonly ZusterArtikel[]): string | undefined {
  if (artikel.image) return artikel.image;
  const eigen = naamWoorden(artikel.name);
  if (!eigen.length) return undefined;

  const eigenZonderMaten = naamZonderMaten(eigen);
  if (!eigenZonderMaten) return undefined; // een naam van louter cijfers zegt niets

  let beste: { slug: string; naam: string; image: string } | undefined;
  for (const kandidaat of catalogus) {
    if (!kandidaat.image || kandidaat.slug === artikel.slug) continue;
    if (!zelfdeSerie(artikel, kandidaat)) continue;
    const woorden = naamWoorden(kandidaat.name);
    if (naamZonderMaten(woorden) !== eigenZonderMaten) continue; // alleen maat/getal mag verschillen
    if (kleurTermen(woorden) !== kleurTermen(eigen)) continue; // nooit een foto in een andere kleur
    const rij = { slug: kandidaat.slug, naam: kandidaat.name, image: kandidaat.image };
    if (
      !beste ||
      rij.naam.length < beste.naam.length ||
      (rij.naam.length === beste.naam.length && rij.slug < beste.slug)
    ) {
      beste = rij;
    }
  }
  return beste?.image;
}

/** Vult in één keer de hele catalogus aan; laat artikelen mét foto ongemoeid. */
export function metZusterFotos<T extends ZusterArtikel>(catalogus: readonly T[]): T[] {
  return catalogus.map((a) => {
    if (a.image) return a;
    const foto = zusterFoto(a, catalogus);
    return foto ? { ...a, image: foto } : a;
  });
}
